import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type WorkflowStepType,
  type WorkflowStepUser,
  type WorkflowStepInstanceCommentType,
  WorkflowStepInstanceCommentTypes,
  WorkflowStepUserPermissions,
  WorkflowStepUserResponses,
} from "~/api/types";
import { Button } from "~/atoms/button";
import { Card } from "~/atoms/card";
import { Label } from "~/atoms/label";
import { Textarea } from "~/atoms/textarea";
import { toast } from "react-toastify";
import { createWorkflowStepInstanceComment } from "~/api/v2/requests";
import { usePersistentState } from "~/hooks/usePersistentState";

type Props = {
  currentUserInStep: WorkflowStepUser;
  stepId: number;
  instanceId: number;
  stepType: WorkflowStepType;
  onUserResponse: () => void;
};

export function UserResponseForm({
  currentUserInStep,
  stepId,
  instanceId,
  stepType,
  onUserResponse,
}: Props) {
  const { t } = useTranslation();
  const [comment, setComment, clearComment] = usePersistentState<string>(
    `workflow_step_user_response:${instanceId}`,
    "",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCommentMissingForReject, setIsCommentMissingForReject] =
    useState(false);

  const handleResponse = async (type: WorkflowStepInstanceCommentType) => {
    if (type === WorkflowStepInstanceCommentTypes.rejected && !comment.trim()) {
      setIsCommentMissingForReject(true);
      return;
    }

    try {
      setIsSubmitting(true);
      setIsCommentMissingForReject(false);
      await createWorkflowStepInstanceComment(stepId, type, comment || null);
      clearComment();
      onUserResponse();
    } catch (error) {
      console.error(
        t(($) => $.commons.workflow.failedToUpdateWorkflowStep),
        error,
      );
      toast.error(t(($) => $.commons.workflow.failedToUpdateWorkflowStep));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="bg-background ring-warning/30 p-0">
      <div className="bg-warning/10 flex flex-col gap-2 p-3">
        <div className="text-warning-foreground dark:text-warning-strong flex items-center gap-2">
          {t(($) => $.commons.workflow.reviewRespond)}
        </div>
        <div className="text-warning-foreground dark:text-warning-strong text-xs">
          {stepType === "approval"
            ? t(($) => $.commons.workflow.approvalDescription)
            : currentUserInStep.permission === WorkflowStepUserPermissions.write
              ? t(($) => $.commons.workflow.writeDescription)
              : t(($) => $.commons.workflow.readDescription)}
        </div>

        <div>
          <Label
            htmlFor="response-comment"
            className="text-warning-foreground dark:text-warning-strong"
          >
            {t(($) => $.commons.workflow.comment)}
          </Label>
          <Textarea
            id="response-comment"
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              if (isCommentMissingForReject && e.target.value.trim()) {
                setIsCommentMissingForReject(false);
              }
            }}
            disabled={isSubmitting}
            className="bg-background min-h-12.5"
          />
          {isCommentMissingForReject && (
            <p className="text-destructive text-sm">
              {t(($) => $.validations.workflow.rejectCommentRequired)}
            </p>
          )}
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() =>
                handleResponse(WorkflowStepInstanceCommentTypes.free)
              }
              disabled={isSubmitting || !comment.trim()}
              size="sm"
            >
              {t(($) => $.commons.workflow.respond.comment)}
            </Button>
            {currentUserInStep.responseStatus !==
              WorkflowStepUserResponses.rejected && (
              <Button
                variant="destructive"
                onClick={() =>
                  handleResponse(WorkflowStepInstanceCommentTypes.rejected)
                }
                disabled={isSubmitting || isCommentMissingForReject}
                size="sm"
              >
                {t(($) => $.commons.workflow.respond.reject)}
              </Button>
            )}
            {currentUserInStep.responseStatus !==
              WorkflowStepUserResponses.approved && (
              <Button
                onClick={() =>
                  handleResponse(WorkflowStepInstanceCommentTypes.approved)
                }
                disabled={isSubmitting}
                size="sm"
              >
                {t(($) => $.commons.workflow.respond.approve)}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
